import React from 'react'
import { useTranslation } from 'react-i18next'

// Links inside Ask CL chat bubbles. Coach answers are LLM output that can be
// steered by what the model read (files, web pages, comments), so a link in
// one never loads anything by itself: only pictures in this app's own media
// storage get a preview, every other link stays a link.

const IMAGE_PATH = /\.(png|jpe?g|gif|bmp|webp|svg)$/i
// Folders of the media storage holding user uploads and coach-made files
const MEDIA_FOLDERS = /^\/(uploads|exports)\//

interface MarkdownLinkProps {
  href?: string
  children?: React.ReactNode
}

/** A file in this app's media storage (uploads, exports), or null for any other link. */
export const mediaFileUrl = (href?: string): URL | null => {
  const host = (process.env.REACT_APP_MEDIA_BUCKET || '').trim().toLowerCase()
  if (!host || !href) return null
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return null
  }
  if (url.protocol !== 'https:' || url.username || url.password) return null
  if (url.host.toLowerCase() !== host) return null
  return MEDIA_FOLDERS.test(url.pathname) ? url : null
}

/** A picture in this app's media storage: the only kind of image a chat bubble loads. */
export const isMediaImage = (href?: string): boolean => {
  const url = mediaFileUrl(href)
  return !!url && !url.search && IMAGE_PATH.test(url.pathname)
}

/** The link's text, for an image's alt text. */
const textOf = (children: React.ReactNode): string =>
  React.Children.toArray(children)
    .filter((child) => typeof child === 'string' || typeof child === 'number')
    .join('')

const FileIcon = () => {
  const { t } = useTranslation()
  return (
    <span role="img" aria-label={t('askCL.fileAttachment')} className="text-lg">
      📄
    </span>
  )
}

/**
 * Links in the user's own bubble (white on purple): their uploaded pictures
 * get a preview, their other uploads a file chip, any other link stays a link.
 */
export const UserMessageLink = ({ href, children }: MarkdownLinkProps) => {
  if (isMediaImage(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block text-center m-1"
      >
        <img
          src={href}
          alt={textOf(children)}
          className="max-w-[120px] max-h-[120px] rounded mb-1"
        />
        <span className="block text-xs text-white">{children}</span>
      </a>
    )
  }
  if (mediaFileUrl(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 bg-white/10 mt-3 border border-white/25 rounded-lg px-3 py-1.5 text-white font-medium no-underline my-1 text-sm transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
      >
        <FileIcon />
        <span>{children}</span>
      </a>
    )
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-white underline break-words"
    >
      {children}
    </a>
  )
}

/**
 * Links in a coach answer (dark text on light grey): a picture from this
 * app's storage gets a preview, any other file of it a file chip (e.g. a
 * document the coach made), and every other link (web sources…) is a plain
 * readable link.
 */
export const BotMessageLink = ({ href, children }: MarkdownLinkProps) => {
  if (isMediaImage(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block text-center m-1"
      >
        <img
          src={href}
          alt={textOf(children)}
          className="max-w-[120px] max-h-[120px] rounded mb-1"
        />
        <div className="text-xs text-app-grey-100">{children}</div>
      </a>
    )
  }
  if (mediaFileUrl(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 bg-white border border-app-grey-40 rounded-lg px-3 py-1.5 text-app-grey-100 font-medium no-underline my-1 text-sm transition-colors hover:bg-app-grey-10 focus:outline-none focus:ring-2 focus:ring-app-primary-60"
      >
        <FileIcon />
        <span>{children}</span>
      </a>
    )
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-app-blue-600 underline break-words hover:text-app-blue-800"
    >
      {children}
    </a>
  )
}
